<script setup>
/**
 * My coverage (XN-4556, XN-4669): every policy this person holds with the
 * agency, soonest renewal first — cards up to four, rows above that with a
 * List / Cards switch — then the files sent to them by name, then who to ask.
 * Never a sideways scroller: a strip hides policies off the edge and gives no
 * sense of how many there are. Prototype `cover()`, build 30 Sep 12:40.
 */
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { onBeforeRouteLeave, RouterLink } from 'vue-router';
import XlButton from '../xl/XlButton.vue';
import XlSegmented from '../xl/XlSegmented.vue';
import XlSkeleton from '../xl/XlSkeleton.vue';
import IconMail from '../icons/IconMail.vue';
import CoverageDocuments from './CoverageDocuments.vue';
import CoveragePolicyCard from './CoveragePolicyCard.vue';
import CoveragePolicyRow from './CoveragePolicyRow.vue';
import { myPolicies } from '../../api/portal';
import { reportError } from '../../faro';
import { useAuthStore } from '../../stores/auth';
import { useEventStore } from '../../stores/event';
import { countLine, layoutFor, rememberView, ROWS_ABOVE, storedView, whoToAsk } from '../../helpers/coverage';

const VIEWS = [{ value: 'list', label: 'List' }, { value: 'cards', label: 'Cards' }];

const auth = useAuthStore();
const eventStore = useEventStore();

const state = ref('loading'); // loading | ready | error | refused
const answer = ref(null);
const now = ref(new Date());
const chosen = ref(null);

const userId = computed(() => auth.userData?.subdomain_user_id ?? null);
const policies = computed(() => answer.value?.policies ?? []);
const broker = computed(() => answer.value?.broker ?? null);
const agency = computed(() => answer.value?.agency?.name || eventStore.event?.value?.login_event_name || 'your agency');
const ask = computed(() => whoToAsk(broker.value, agency.value));
const layout = computed(() => layoutFor(policies.value.length, chosen.value));
const switchable = computed(() => policies.value.length > ROWS_ABOVE);

function choose(view) {
  chosen.value = view;
  rememberView(userId.value, view);
}

async function load() {
  state.value = 'loading';
  try {
    answer.value = await myPolicies();
    now.value = new Date();
    state.value = 'ready';
  } catch (error) {
    // Staff previewing the portal are refused /my/* by design.
    if (error?.response?.status === 403) {
      state.value = 'refused';
      return;
    }
    reportError(error);
    state.value = 'error';
  }
}

// Back from a policy lands where the list was left. The router keeps no scroll
// (it has no scrollBehavior), so the leave saves it in this history entry and
// the list puts it back once drawn, as the Library does.
let restoreTo = window.history.state?.coverageScroll ?? null;
watch(state, async (s) => {
  if (s !== 'ready' || restoreTo == null) return;
  const top = restoreTo;
  restoreTo = null;
  await nextTick();
  window.scrollTo(0, top);
});
onBeforeRouteLeave(() => {
  window.history.replaceState({ ...window.history.state, coverageScroll: window.scrollY }, '');
});

onMounted(() => {
  chosen.value = storedView(userId.value);
  load();
});
</script>

<template>
  <section class="cov">
    <h1 class="cov__title">What you're covered for</h1>
    <p v-if="state === 'ready' && policies.length" class="cov__sub" :class="{ 'cov__sub--tight': switchable }">
      {{ countLine(policies.length, agency) }}
    </p>

    <div v-if="state === 'loading'" class="cov__loading"><XlSkeleton v-for="n in 2" :key="n" height="236px" /></div>

    <div v-else-if="state === 'error'" class="cov__error" role="alert">
      <p>Your policies can't be shown right now.</p>
      <XlButton variant="ghost" size="sm" data-testid="coverage-retry" @click="load()">Retry</XlButton>
    </div>

    <p v-else-if="state === 'refused'" class="cov__calm" data-testid="coverage-refused">My coverage shows a policyholder their own policies.</p>

    <div v-else-if="!policies.length" class="cov__card cov__empty" data-testid="coverage-empty">
      <template v-if="ask.has">
        <p>No policies on file yet. {{ ask.canAdd }}</p>
        <RouterLink to="/hub/messages" class="cov__btn">{{ ask.message }}</RouterLink>
      </template>
      <p v-else>No policies on file yet. {{ ask.noBroker }}</p>
    </div>

    <template v-else>
      <div v-if="switchable" class="cov__switch">
        <XlSegmented :model-value="layout" :options="VIEWS" aria-label="Show policies as" @update:model-value="choose" />
      </div>
      <div v-if="layout === 'list'" class="cov__card cov__rows">
        <CoveragePolicyRow v-for="policy in policies" :key="policy.id" :policy="policy" :now="now" />
      </div>
      <div v-else class="cov__grid">
        <CoveragePolicyCard v-for="policy in policies" :key="policy.id" :policy="policy" :now="now" />
      </div>
    </template>

    <CoverageDocuments v-if="state !== 'refused' && state !== 'loading'" :sender="ask.sender" />

    <template v-if="state === 'ready' && policies.length">
      <div class="cov__sh"><h2>Something not right?</h2><i class="cov__ln" /></div>
      <div class="cov__card cov__ask" data-testid="coverage-ask">
        <span class="cov__ask-ico" aria-hidden="true"><IconMail color="currentColor" size="25" /></span>
        <span v-if="ask.has" class="cov__ask-t">
          <b>Ask {{ ask.name }}</b>
          <span>{{ ask.canChange }}</span>
        </span>
        <span v-else class="cov__ask-t"><span>{{ ask.noBroker }}</span></span>
        <RouterLink v-if="ask.has" to="/hub/messages" class="cov__btn">{{ ask.message }}</RouterLink>
      </div>
    </template>
  </section>
</template>

<style scoped>
.cov__title { font-size: 32px; font-weight: 600; letter-spacing: -0.03em; margin: 0 0 8px; }
.cov__sub { font-size: 16.5px; color: var(--xl-color-on-surface-variant); margin: 0 0 26px; }
.cov__sub--tight { margin-bottom: 14px; }
.cov__switch { margin: 0 0 14px; }
.cov__loading { display: grid; gap: 14px; grid-template-columns: repeat(auto-fill, minmax(min(290px, 100%), 1fr)); }
.cov__grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(min(290px, 100%), 1fr)); gap: 14px; margin-bottom: 26px; }
.cov__card {
  background: #fff; border: 1px solid var(--xl-color-border); border-radius: var(--xl-radius-lg);
  box-shadow: var(--xl-shadow-1); margin-bottom: 14px;
}
.cov__rows { padding: 0; overflow: hidden; margin-bottom: 26px; }
.cov__empty { padding: 22px 24px; display: flex; gap: 16px; align-items: center; flex-wrap: wrap; justify-content: space-between; }
.cov__empty p { margin: 0; font-size: 15px; color: var(--xl-color-on-surface-variant); flex: 1 1 260px; }
.cov__calm { margin: 0; color: var(--xl-color-on-surface-variant); }
.cov__error {
  display: flex; align-items: center; justify-content: space-between; gap: 14px; flex-wrap: wrap; padding: 18px 20px;
  border-radius: 16px; font-size: 14.5px; background: var(--xl-color-error-container, #FBEAEA); border: 1px solid var(--xl-color-error); color: var(--xl-color-error);
}
.cov__error p { margin: 0; }
.cov__sh { display: flex; align-items: baseline; gap: 12px; margin: 34px 0 14px; }
.cov__sh h2 { margin: 0; font-size: 13px; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase; color: var(--xl-color-outline-variant); }
.cov__ln { flex: 1; height: 1px; background: var(--xl-color-border); }
.cov__ask { padding: 22px 24px; display: flex; gap: 16px; align-items: center; flex-wrap: wrap; }
.cov__ask-ico { width: 46px; height: 46px; flex: 0 0 46px; border-radius: 13px; display: grid; place-items: center; background: var(--xl-color-primary-container); color: var(--xl-color-primary); }
.cov__ask-t { flex: 1; min-width: 180px; }
.cov__ask-t b { display: block; font-size: 15.5px; }
.cov__ask-t span { font-size: 13.5px; color: var(--xl-color-outline); }
.cov__btn {
  display: inline-flex; align-items: center; justify-content: center; height: 42px; padding: 0 20px; border-radius: 99px;
  font-size: 14.5px; font-weight: 600; color: #fff; text-decoration: none;
  background: linear-gradient(155deg, #8154A6, var(--xl-color-primary)); box-shadow: 0 5px 16px rgba(94, 47, 120, 0.26);
}
.cov__btn:focus-visible { outline: 2px solid var(--xl-color-primary); outline-offset: 2px; }
@media (max-width: 480px) { .cov__title { font-size: 28px; } .cov__empty, .cov__ask { padding: 18px; } }
</style>
